import { z } from "zod";
import {
  personDocumentSchema,
  personProfileDocumentSchema,
  personInternalDocumentSchema,
} from "@event-planner/shared";

/**
 * Admin person editor (ADR-136) — single page form presented as tabs.
 *
 * Mirrors the EventForm pattern: ONE `useForm` lives at the page level
 * (`UserDetailPage`), tabs are pure section components that consume the
 * form via `useFormContext`. RHF v7's default `shouldUnregister: false`
 * keeps unmounted field values in form state, so switching tabs preserves
 * in-progress edits without any extra plumbing.
 *
 * Per ADR-083, the canonical document shapes live in
 * `packages/shared/src/schemas/{person,person-profile,person-internal}.ts`.
 * The schema below picks the editable subset across all three tiers; the
 * page-level submit handler dispatches the writes per tier:
 *  - `persons` (name parts, honorifics) via `updateUserOnBehalf`
 *  - `personProfiles` (title, org, bio, contact, dietary, accessibility)
 *    via `updateUserOnBehalf`
 *  - `personInternals` (notes) via `savePersonInternal`
 *  - `persons.role` (Auth claim) via `setUserRole` (only if changed)
 *
 * The single editor handles both claimed and pre-claim persons uniformly,
 * since under ADR-136 there is one `persons` collection covering both.
 *
 * See ADR-002 (three-tier user data) and ADR-044 (binary privacy model).
 */
export const userEditSchema = z.object({
  // From `persons`
  ...personDocumentSchema.pick({
    honorificPrefix: true,
    honorificSuffix: true,
    role: true,
  }).shape,
  // From `personProfiles`
  ...personProfileDocumentSchema.pick({
    title: true,
    organization: true,
    workCity: true,
    workState: true,
    bio: true,
    phone: true,
    linkedIn: true,
    website: true,
    dietaryRestrictions: true,
    accessibilityNeeds: true,
  }).shape,
  // From `personInternals`
  ...personInternalDocumentSchema.pick({
    notes: true,
  }).shape,
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
});

export type UserEditFormData = z.infer<typeof userEditSchema>;
